import type { AxiosResponse } from 'axios'
import { apiDjango, unwrap } from './ApiDjango'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'
import {
  ImportApiResult,
  ImportCreateResponseDTO,
  ImportFileUrlDTO,
  ImportHeaderDTO,
  ImportInsertRequestDTO,
  ImportListPageDTO,
  ImportListParams,
  ImportProgressDTO,
  ImportUpdateRequestDTO,
  ShipmentExistingRequestDTO,
  ShipmentExistingResponseDTO,
  BulkUploadManifestRequestDTO,
  BulkUploadManifestResponseDTO,
} from '../interfaces/Import/ShipmentBulkUploadApi'

const { REACT_APP_URL_PATH_API_DJANGO } = process.env
const IMPORT_BASE = `${REACT_APP_URL_PATH_API_DJANGO}/shipment-bulk`

export async function insertShipmentList(payload: {
  shipment_list: any[]
}): Promise<ListResponse<any>> {
  try {
    const url = `${REACT_APP_URL_PATH_API_DJANGO}/shipment`
    const resp = await apiDjango.post(url, payload)
    if (
      resp.status === 200 &&
      resp.data &&
      (resp.data as any).error === false
    ) {
      return {
        didError: false,
        message: (resp.data as any).message ?? 'OK',
        errorMessage: '',
        model: (resp.data as any).data,
      }
    }
    const d = resp.data as any
    const msg =
      d?.message ||
      d?.detail ||
      JSON.stringify(d?.errors) ||
      'Error creando guías'
    return { didError: true, message: msg, errorMessage: msg, model: null }
  } catch (error: any) {
    const msg = error?.message || 'Error llamando al API de guías'
    return { didError: true, message: msg, errorMessage: msg, model: null }
  }
}

// ---------------------------------------------------------------------------
// Importación asíncrona de guías — /api/shipment/bulk-upload/*
// ---------------------------------------------------------------------------


/**
 * POST /shipment/bulk-upload/ — crea una importación.
 * Con archivo → multipart/form-data (payload = JSON.stringify(dto), file). NO setear
 * Content-Type: el browser arma el boundary (spec §6.4). Sin archivo → JSON directo.
 */
export function createImport(args: {
  dto: ImportInsertRequestDTO
  file?: File | null
}): Promise<ImportApiResult<ImportCreateResponseDTO>> {
  const { dto, file } = args
  const url = `${IMPORT_BASE}`
  if (file) {
    const form = new FormData()
    form.append('payload', JSON.stringify(dto))
    form.append('file', file)
    return unwrap<ImportCreateResponseDTO>(apiDjango.post(url, form))
  }
  return unwrap<ImportCreateResponseDTO>(apiDjango.post(url, dto))
}

/** GET /shipment/bulk-upload/list — listado paginado con filtros. */
export function getImportList(
  params: ImportListParams = {}
): Promise<ImportApiResult<ImportListPageDTO>> {
  return unwrap<ImportListPageDTO>(
    apiDjango.get(`${IMPORT_BASE}/list`, { params })
  )
}

/** GET /shipment/bulk-upload/<id> — detalle completo (header + details + logs). */
export function getImport(
  headerId: string
): Promise<ImportApiResult<ImportHeaderDTO>> {
  return unwrap<ImportHeaderDTO>(apiDjango.get(`${IMPORT_BASE}/${headerId}`))
}

/** GET /shipment/bulk-upload/<id>/progress — endpoint del polling (liviano). */
export function getImportProgress(
  headerId: string
): Promise<ImportApiResult<ImportProgressDTO>> {
  return unwrap<ImportProgressDTO>(
    apiDjango.get(`${IMPORT_BASE}/${headerId}/progress`)
  )
}

/** GET /shipment/bulk-upload/<id>/file — URL firmada (expira 15 min). */
export function getImportFile(
  headerId: string
): Promise<ImportApiResult<ImportFileUrlDTO>> {
  return unwrap<ImportFileUrlDTO>(
    apiDjango.get(`${IMPORT_BASE}/${headerId}/file`)
  )
}

/**
 * PUT /shipment/bulk-upload/<id>/update — corrige líneas en error in-place.
 * Body: { corrections: [{ shipment_bulk_upload_detail_id, shipment }] }. Cada corrección
 * pasa el detalle de 108 (Error) → 106 (Pending), limpia el error y lo reprocesa.
 * NO crea líneas nuevas (eso lo hacía el viejo body { shipment_list }).
 */
export function updateImport(
  headerId: string,
  body: ImportUpdateRequestDTO
): Promise<ImportApiResult<any>> {
  return unwrap<any>(apiDjango.put(`${IMPORT_BASE}/${headerId}/update`, body))
}

/** POST /shipment/bulk-upload/<id>/execute — reintento manual (retry_errors_only por defecto). */
export function executeImport(
  headerId: string,
  retryErrorsOnly = true
): Promise<ImportApiResult<any>> {
  return unwrap<any>(
    apiDjango.post(`${IMPORT_BASE}/${headerId}/execute`, {
      retry_errors_only: retryErrorsOnly,
    })
  )
}

/** POST /shipment/bulk-upload/<id>/cancel — cancela (solo si InProcess). */
export function cancelImport(headerId: string): Promise<ImportApiResult<any>> {
  return unwrap<any>(apiDjango.post(`${IMPORT_BASE}/${headerId}/cancel`, {}))
}

/**
 * POST /shipment/existing — ¿cuáles de estas guías ya existen?
 * Compara (case-insensitive) contra ShipmentNumber y ShipmentHeaderReference y devuelve
 * en `data.existing_shipment_identifiers` solo las que existen (con su casing
 * almacenado). Las faltantes se deducen: input - existentes. Máx. 5000 identificadores
 * por llamada (el backend valida tras normalizar). Envelope estándar → unwrap; en error,
 * model queda null (el caller no marca duplicados en vez de fallar la carga).
 *
 * El alcance depende de permisos (`shipment.existence_check`, y `shipment.read_all` para
 * ver más allá de lo propio) y un `account_code` inválido responde `account.AccountSite.1`:
 * ver las notas de `ShipmentExistingRequestDTO`.
 */
export function getShipmentsExisting(
  body: ShipmentExistingRequestDTO
): Promise<ImportApiResult<ShipmentExistingResponseDTO>> {
  return unwrap<ShipmentExistingResponseDTO>(
    apiDjango.post(`${REACT_APP_URL_PATH_API_DJANGO}/shipment/existing`, body)
  )
}

/**
 * POST /shipment-bulk/<id>/manifest — vincula la carga con su manifiesto.
 *
 * Se llama DESPUÉS de que `CreateManifest` del API .NET respondió OK. El
 * manifiesto real vive en .NET; esto solo deja el vínculo del lado Django.
 *
 * ES IDEMPOTENTE: llamarlo de nuevo refresca la fecha y el vínculo, y no exige
 * un estado particular de la carga. Por eso el reintento manual del detalle es
 * seguro.
 *
 * El body lleva SOLO `manifest_id`; el header va en la URL. El GUID se acepta en
 * mayúsculas o minúsculas.
 *
 * Errores propios:
 *   shipping.ShipmentBulkUploadHeader.1   la carga masiva no existe
 *   shipping.ShipmentBulkUploadHeader.11  la carga es de otro usuario
 *   logistics.MasterShipmentHeader.1      el manifiesto no existe
 *
 * Si falla, el manifiesto YA existe: el llamador NO puede presentarlo como un
 * fallo de creación. Muestra el éxito igual y avisa aparte que quedó sin
 * vincular, con el Nº a mano para conciliar.
 */
export function createBulkUploadManifest(
  headerId: string,
  manifestId: string
): Promise<ImportApiResult<BulkUploadManifestResponseDTO>> {
  return unwrap<BulkUploadManifestResponseDTO>(
    apiDjango.post(`${IMPORT_BASE}/${headerId}/manifest`, {
      manifest_id: manifestId,
    } as BulkUploadManifestRequestDTO)
  )
}

/** DELETE /shipment/bulk-upload/<id>/delete — soft-delete (solo InProcess y 0 Executed). */
export function deleteImport(headerId: string): Promise<ImportApiResult<any>> {
  return unwrap<any>(apiDjango.delete(`${IMPORT_BASE}/${headerId}/delete`))
}

/** DELETE /shipment/bulk-upload/<id>/details/<detailId> — borra un detalle (si no Executed). */
export function deleteImportDetail(
  headerId: string,
  detailId: string
): Promise<ImportApiResult<any>> {
  return unwrap<any>(
    apiDjango.delete(`${IMPORT_BASE}/${headerId}/details/${detailId}`)
  )
}
